import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiSave } from "react-icons/fi";
import libraryService from "../../services/libraryService";

const MAX_QR_SIZE = 2 * 1024 * 1024;

export default function LibrarySettings() {
    const [form, setForm] = useState({
        finePerDay: 2,
        maxBooksPerStudent: 3,
        loanPeriodDays: 14,
        maxRenewals: 1,
        upiId: "",
        qrCodeUrl: "",
        qrCodeDataUrl: "",
    });
    const [fetching, setFetching] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingQR, setSavingQR] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await libraryService.getSettings();
                setForm((prev) => ({
                    ...prev,
                    finePerDay: res.data.finePerDay ?? 2,
                    maxBooksPerStudent: res.data.maxBooksPerStudent ?? 3,
                    loanPeriodDays: res.data.loanPeriodDays ?? 14,
                    maxRenewals: res.data.maxRenewals ?? 1,
                    upiId: res.data.upiId || "",
                    qrCodeUrl: res.data.qrCodeUrl || "",
                }));
            } catch (err) {
                toast.error(err.message);
            } finally {
                setFetching(false);
            }
        })();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await libraryService.updateSettings({
                finePerDay: Number(form.finePerDay),
                maxBooksPerStudent: Number(form.maxBooksPerStudent),
                loanPeriodDays: Number(form.loanPeriodDays),
                maxRenewals: Number(form.maxRenewals),
                upiId: form.upiId,
            });
            toast.success("Library settings saved.");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const readFileAsDataUrl = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Failed to read image file."));
            reader.readAsDataURL(file);
        });

    const handleQRChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Please choose a valid image file.");
            e.target.value = "";
            return;
        }
        if (file.size > MAX_QR_SIZE) {
            toast.error("QR image must be 2 MB or smaller.");
            e.target.value = "";
            return;
        }

        try {
            const dataUrl = await readFileAsDataUrl(file);
            setSavingQR(true);
            const res = await libraryService.uploadQRCode(dataUrl);
            setForm((prev) => ({ ...prev, qrCodeUrl: res.data.qrCodeUrl, qrCodeDataUrl: "" }));
            toast.success("QR code updated.");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSavingQR(false);
            e.target.value = "";
        }
    };

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading library settings...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Library Settings</h1>
                    <p className="page-subtitle">Configure fine rate, loan period, borrowing limits, and the payment QR code.</p>
                </div>
            </div>

            <form onSubmit={handleSave}>
                <div className="card" style={{ marginBottom: "1.25rem" }}>
                    <div className="card-header">
                        <strong>Borrowing Rules</strong>
                    </div>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Fine per Day (₹)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    name="finePerDay"
                                    min="0"
                                    value={form.finePerDay}
                                    onChange={handleChange}
                                />
                                <span className="form-hint">Charged for each day a book is returned after its due date.</span>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Loan Period (days)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    name="loanPeriodDays"
                                    min="1"
                                    value={form.loanPeriodDays}
                                    onChange={handleChange}
                                />
                                <span className="form-hint">Default due date = issue date + this many days.</span>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Max Books per Student</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    name="maxBooksPerStudent"
                                    min="1"
                                    value={form.maxBooksPerStudent}
                                    onChange={handleChange}
                                />
                                <span className="form-hint">A student can't issue more than this many books at once.</span>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Max Renewals</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    name="maxRenewals"
                                    min="0"
                                    value={form.maxRenewals}
                                    onChange={handleChange}
                                />
                                <span className="form-hint">Reserved for future renewal feature.</span>
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.25rem" }}>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                <FiSave size={14} style={{ marginRight: 6 }} />
                                {saving ? "Saving..." : "Save Rules"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            <div className="card">
                <div className="card-header">
                    <strong>Payment QR Code</strong>
                </div>
                <div className="card-body">
                    <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start", flexWrap: "wrap" }}>
                        <div
                            style={{
                                width: 160,
                                height: 160,
                                borderRadius: 12,
                                overflow: "hidden",
                                border: "1px solid var(--border)",
                                background: "#f8fafc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "var(--text-muted)",
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                        >
                            {form.qrCodeUrl ? (
                                <img src={form.qrCodeUrl} alt="Payment QR" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                            ) : (
                                <span>No QR Uploaded</span>
                            )}
                        </div>
                        <div style={{ flex: 1, minWidth: 240 }}>
                            <div className="form-group">
                                <label className="form-label">Upload / Replace QR Code</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control"
                                    onChange={handleQRChange}
                                    disabled={savingQR}
                                    style={{ maxWidth: 320 }}
                                />
                                <span className="form-hint">
                                    Shown to students in their portal to pay late fees. Uploads and saves immediately.
                                </span>
                            </div>
                            <div className="form-group">
                                <label className="form-label">UPI ID (fallback text)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="upiId"
                                    placeholder="e.g. college@upi"
                                    value={form.upiId}
                                    onChange={handleChange}
                                    style={{ maxWidth: 320 }}
                                />
                                <span className="form-hint">Shown alongside the QR in case scanning fails. Saved with "Save Rules" above.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}